import { css } from "@emotion/css";
import { ExternalLink } from "@octopusdeploy/design-system-components";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useEffect } from "react";
import { contentContainerStyles, sectionDescriptionStyles } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import { CodeEditor, TextFormat } from "~/components/CodeEditor/CodeEditor";
import { Note } from "~/components/form";
interface ConfigurationModel {
    DefaultClusterRolePermissions: string | null;
}
type DefaultPermissionsPageProps = {
    model: ConfigurationModel;
    onModelChange: (model: ConfigurationModel) => void;
    getFieldError: (fieldName: string) => string;
    configurePermissions: true;
} | {
    configurePermissions: false;
    model?: undefined;
    onModelChange?: undefined;
    getFieldError?: undefined;
};
export const DefaultPermissionsPage = ({ model, onModelChange, getFieldError, configurePermissions }: DefaultPermissionsPageProps) => {
    useEffect(() => {
        if (configurePermissions && !model.DefaultClusterRolePermissions) {
            onModelChange({
                DefaultClusterRolePermissions: `rules: []`,
            });
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<div className={contentContainerStyles}>
            <div>
                <div>If a workload's scope doesn't match any Workload Service Accounts, it will get the empty default permission set.</div>
                <div className={styles.listHeading}>To customize the default permissions:</div>
                <ol className={styles.list}>
                    <li>
                        <ExternalLink href="GranularAgentPermissionsExamples" size="large">
                            View examples for common use cases
                        </ExternalLink>
                    </li>
                    <li>
                        <ExternalLink href="GranularAgentPermissions" size="large">
                            Read the documentation
                        </ExternalLink>
                    </li>
                    <li>{configurePermissions ? "Add a new default permission set below" : "Set the Helm values for each Kubernetes agent in your cluster"}</li>
                </ol>
            </div>
            {configurePermissions ? (<>
                    <div className={getFieldError("DefaultClusterRolePermissions") ? styles.editorError : undefined}>
                        <CodeEditor value={model.DefaultClusterRolePermissions ?? ""} lineWrapping={false} showCopyButton showLineNumbers={false} language={TextFormat.YAML} allowFullScreen settingsOverride={{ theme: "default", wordWrap: false }} onChange={(value) => {
                onModelChange({ DefaultClusterRolePermissions: value });
            }}/>
                    </div>
                    {getFieldError("DefaultClusterRolePermissions") && <div className={styles.errorText}>{getFieldError("DefaultClusterRolePermissions")}</div>}
                    <p className={sectionDescriptionStyles}>Octopus will apply these permissions via Helm values</p>
                </>) : (<Note>Run a health check on your agent to update the detected configuration.</Note>)}
        </div>);
};
const styles = {
    listHeading: css({ font: text.body.bold.large, marginTop: space[24] }),
    list: css({
        listStyleType: "decimal",
        paddingLeft: space[24],
        margin: 0,
    }),
    editorError: css({
        border: `${borderWidth["1"]} solid ${themeTokens.color.border.danger}`,
        borderRadius: borderRadius.medium,
    }),
    errorText: css({
        color: themeTokens.color.text.danger,
        font: text.body.regular.medium,
        marginTop: space[8],
    }),
};
